import React, { useRef, useState } from "react";
import { useQueryClient } from "react-query";
import useAuth from "hooks/useAuth";
import { isDpatDistrictRestrictedViewer } from "config/contextModules";
import DpatExportButtons from "pages/dpat-dashboard/components/DpatExportButtons";
import {
  downloadExcelSheets,
  downloadPagedNodeAsPdf,
} from "pages/dpat-dashboard/dpatDashboardExport";
import {
  getCommentsAndGapsSummary,
  getCommentsByTable,
  useGetScoreSheetSummary,
} from "service/comment.service";
import DpatCircleProgressLoader from "./DpatCircleProgressLoader";
import ScoreSheetSummary from "./ScoreSheetSummary";
import DpatSubIndicatorScoresSummary from "./DpatSubIndicatorScoresSummary";
import CommentAndGabsSummary from "./CommentAndGabsSummary";
import QualityAssuranceCommentsSummary from "./QualityAssuranceCommentsSummary";

const SCORE_SHEET_NAMES = ["Summary", "CI Details", "SDI Details", "PI Details"];

function sheetNameForTable(table, index) {
  const group = table.closest("[data-export-sheet]")?.getAttribute("data-export-sheet");
  if (group === "annex-5") {
    const groupTables = [...table.closest("[data-export-sheet]").querySelectorAll("table")];
    return groupTables.indexOf(table) === 0 ? "Annex 5 Comments" : "Annex 6 Gaps";
  }
  if (group === "qa-comments") return "QA Comments";
  return SCORE_SHEET_NAMES[index] || `Sheet ${index + 1}`;
}

function tablesToExcelSheets(root) {
  return [...(root?.querySelectorAll("table") || [])]
    .map((table, index) => ({
      name: sheetNameForTable(table, index),
      rows: [...table.querySelectorAll("tr")].map((tr) =>
        [...tr.querySelectorAll("th, td")].map((cell) => String(cell.innerText || "").trim()),
      ),
    }))
    .filter((sheet) => sheet.rows.length);
}

const DpatScoreSheetsBlock = ({
  district,
  districtId,
  year,
  region,
  hideRoleScoreColumns = false,
  assessmentStartTables = null,
  hideLoader = false,
}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data: scoreResponse, isLoading: scoresLoading } = useGetScoreSheetSummary(
    districtId,
    year,
    "DPAT",
  );
  const exportRef = useRef(null);
  const commentsRef = useRef(null);
  const [loadExportComments, setLoadExportComments] = useState(false);
  const includeComments = !isDpatDistrictRestrictedViewer(user);
  const districtLabel = district?.label || district?.name || "District";
  const commentsDistrictId = district?.value ?? districtId;
  const filenameBase = `dpat-score-sheets-${districtLabel}-${year}`.replace(/\s+/g, "-");

  const handlePdf = async () => {
    if (!exportRef.current) return;
    const comments = commentsRef.current;
    const root = exportRef.current;
    if (includeComments) {
      setLoadExportComments(true);
      await Promise.all([
        queryClient.prefetchQuery(
          ["getCommentsAndGapsSummary", commentsDistrictId, year, "DPAT"],
          () => getCommentsAndGapsSummary(commentsDistrictId, year, "DPAT"),
        ),
        queryClient.prefetchQuery(
          ["getCommentsByTable", commentsDistrictId, year, "DPAT"],
          () => getCommentsByTable(commentsDistrictId, year, "DPAT"),
        ),
      ]).catch(() => {});
    }
    if (comments) comments.style.display = "block";
    const previousWidth = root.style.width;
    const previousMaxWidth = root.style.maxWidth;
    root.classList.add("dpat-pdf-export");
    root.style.width = "1000px";
    root.style.maxWidth = "1000px";
    try {
      await new Promise((resolve) => setTimeout(resolve, 200));
      await downloadPagedNodeAsPdf(root, `${filenameBase}.pdf`, { orientation: "portrait", scale: 1.5 });
    } finally {
      root.classList.remove("dpat-pdf-export");
      root.style.width = previousWidth;
      root.style.maxWidth = previousMaxWidth;
      if (comments) comments.style.display = "none";
    }
  };

  const handleExcel = () => {
    const sheets = tablesToExcelSheets(exportRef.current);
    if (!sheets.length) return;
    downloadExcelSheets(sheets, `${filenameBase}.xlsx`);
  };

  return (
    <div>
      <div
        data-export-hide
        className="mb-3 flex items-center justify-end"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          background: "#fff",
          padding: "8px 0",
        }}
      >
        <DpatExportButtons
          label="Download"
          onPdf={handlePdf}
          onExcel={handleExcel}
        />
      </div>

      {!hideLoader && scoresLoading && !scoreResponse ? (
        <DpatCircleProgressLoader label="Loading scores" />
      ) : (
      <div ref={exportRef}>
        <ScoreSheetSummary
          district={district}
          districtId={districtId}
          year={year}
          hideRoleScoreColumns={hideRoleScoreColumns}
        />
        <DpatSubIndicatorScoresSummary
          district={district}
          districtId={districtId}
          year={year}
          hideRoleScoreColumns={hideRoleScoreColumns}
          assessmentStartTables={assessmentStartTables}
        />
        {includeComments ? (
          <div ref={commentsRef} style={{ display: "none" }}>
            <div data-pdf-part="annex-5" data-export-sheet="annex-5">
              <h3 style={{ textAlign: "center", padding: "10px" }}>
                ANNEX 5 SUMMARY OF COMMENTS
              </h3>
              <CommentAndGabsSummary district={district} region={region} year={year} enabled={loadExportComments} />
            </div>
            <div data-pdf-part="qa-comments" data-export-sheet="qa-comments">
              <h3 style={{ textAlign: "center", padding: "10px" }}>
                SUMMARY OF QUALITY ASSURANCE COMMENTS
              </h3>
              <QualityAssuranceCommentsSummary district={district} year={year} enabled={loadExportComments} />
            </div>
          </div>
        ) : null}
      </div>
      )}
    </div>
  );
};

export default DpatScoreSheetsBlock;
